// client/src/hooks/useContactsCached.js
// Custom hook for contacts with 7-day cache, SWR, and smart polling

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  getContactsCached, 
  setContactsCache, 
  mergeContactIntoCache, 
  clearContactsCache,
  fetchContactsFromApi,
  prefetchContacts
} from '../services/contactsService';
import { 
  claimLeader, 
  renewLeader, 
  releaseLeader, 
  isLeader, 
  addJitter, 
  isTabVisible, 
  isOnline,
  TAB_ID
} from '../utils/visibility';

/**
 * Custom hook for managing contacts with local cache and SWR
 * @param {string} userId - User ID
 * @returns {Object} Contacts state and methods
 */
export function useContactsCached(userId) {
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRevalidating, setIsRevalidating] = useState(false);
  
  // Refs for cleanup and state management
  const pollIntervalRef = useRef(null);
  const leaderRenewalRef = useRef(null);
  const isMountedRef = useRef(true);

  // Fetch contacts with ETag support
  const fetchContacts = useCallback(async (etag = null, isBackground = false) => {
    if (!userId || !isMountedRef.current) return;
    
    try {
      if (isBackground) {
        setIsRevalidating(true);
      }
      
      const { data, etag: newEtag } = await fetchContactsFromApi(etag);
      
      // If data is null, it means 304 Not Modified
      if (data === null) {
        if (isBackground) {
          setIsRevalidating(false);
        }
        return;
      }
      
      // Only update if data actually changed
      if (isMountedRef.current) {
        setContacts(prevContacts => {
          if (JSON.stringify(prevContacts) !== JSON.stringify(data)) {
            setContactsCache(userId, data, newEtag);
            return data;
          }
          return prevContacts;
        });
      }
      
      if (isBackground) {
        setIsRevalidating(false);
      }
    } catch (error) {
      console.warn('Fetch contacts error:', error);
      if (isBackground) {
        setIsRevalidating(false);
      }
    }
  }, [userId]);

  // Load contacts from cache or API
  const loadContacts = useCallback(async () => {
    if (!userId || !isMountedRef.current) return;

    try {
      setError(null);
      
      // 1. Try to load from cache first (synchronous)
      const cached = getContactsCached(userId);
      if (cached) {
        setContacts(cached.data);
        setIsLoading(false);
        
        // 2. Background revalidation (SWR) - debounced
        setTimeout(() => {
          if (isMountedRef.current) {
            fetchContacts(cached.etag, true);
          }
        }, 3000); // 3-second debounce
        return;
      }

      // 3. No cache or expired - fetch from API
      setIsLoading(true);
      await fetchContacts();
    } catch (error) {
      console.error('Failed to load contacts:', error);
      if (isMountedRef.current) {
        setError('Failed to load contacts');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [userId, fetchContacts]);

  // Smart polling with leader election
  const startPolling = useCallback(() => {
    if (!userId || !isMountedRef.current) return;
    
    // Try to claim leadership
    if (!claimLeader()) {
      console.log(`Tab ${TAB_ID}: Not the polling leader, skipping polling`);
      return;
    }
    
    console.log(`Tab ${TAB_ID}: Became polling leader`);
    
    // Renew leadership every 20 seconds
    const renewLeadership = () => {
      if (isMountedRef.current && isTabVisible()) {
        renewLeader();
        leaderRenewalRef.current = setTimeout(renewLeadership, 20000);
      }
    };
    renewLeadership();
    
    // Polling function
    const poll = async () => {
      if (!isMountedRef.current || !isTabVisible() || !isOnline()) {
        return;
      }
      
      // Check if we're still the leader
      if (!isLeader()) {
        console.log(`Tab ${TAB_ID}: Lost leadership, stopping polling`);
        return;
      }
      
      const cached = getContactsCached(userId);
      await fetchContacts(cached?.etag, true);
    };
    
    // Start polling with jitter
    const pollWithJitter = () => {
      if (isMountedRef.current && isTabVisible()) {
        poll();
        const nextInterval = addJitter(25000, 4000); // 25s ± 4s jitter
        pollIntervalRef.current = setTimeout(pollWithJitter, nextInterval);
      }
    };
    
    // Initial poll after 5 seconds
    pollIntervalRef.current = setTimeout(pollWithJitter, 5000);
    
    // Cleanup function
    return () => {
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (leaderRenewalRef.current) {
        clearTimeout(leaderRenewalRef.current);
        leaderRenewalRef.current = null;
      }
      releaseLeader();
    };
  }, [userId, fetchContacts]);

  // Add new contact to cache and state
  const addContact = useCallback((contact) => {
    if (!userId || !contact || !isMountedRef.current) return;
    
    // Merge into cache
    mergeContactIntoCache(userId, contact);
    
    // Update local state immediately
    setContacts(prev => {
      const existingIndex = prev.findIndex(c => c._id === contact._id);
      if (existingIndex >= 0) {
        // Update existing
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...contact };
        return updated;
      } else {
        // Add new at beginning
        return [contact, ...prev];
      }
    });
  }, [userId]);

  // Refresh contacts (force API fetch)
  const refreshContacts = useCallback(async () => {
    if (!userId || !isMountedRef.current) return;
    
    try {
      setIsRevalidating(true);
      await fetchContacts();
    } catch (error) {
      console.error('Failed to refresh contacts:', error);
      if (isMountedRef.current) {
        setError('Failed to refresh contacts');
      }
    } finally {
      if (isMountedRef.current) {
        setIsRevalidating(false);
      }
    }
  }, [userId, fetchContacts]);

  // Clear cache and reset state
  const clearCache = useCallback(() => {
    if (!userId || !isMountedRef.current) return;
    
    clearContactsCache(userId);
    setContacts([]);
    setError(null);
  }, [userId]);

  // Prefetch contacts (non-blocking)
  const prefetch = useCallback(async () => {
    if (!userId || !isMountedRef.current) return;
    
    try {
      await prefetchContacts(userId);
    } catch (error) {
      console.warn('Failed to prefetch contacts:', error);
    }
  }, [userId]);

  // Load contacts on mount and userId change
  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Start polling when user is loaded and tab is visible
  useEffect(() => {
    if (!userId) return;
    
    const cleanup = startPolling();
    return cleanup;
  }, [userId, startPolling]);

  // Handle visibility changes and online events
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (isTabVisible() && isOnline() && userId) {
        console.log('Tab became visible, fetching contacts immediately');
        const cached = getContactsCached(userId);
        fetchContacts(cached?.etag, true);
      }
    };
    
    const handleOnline = () => {
      if (isOnline() && userId) {
        console.log('Browser came online, fetching contacts immediately');
        const cached = getContactsCached(userId);
        fetchContacts(cached?.etag, true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [userId, fetchContacts]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
      }
      if (leaderRenewalRef.current) {
        clearTimeout(leaderRenewalRef.current);
      }
      releaseLeader();
    };
  }, []);

  return {
    contacts,
    isLoading,
    error,
    isRevalidating,
    addContact,
    refreshContacts,
    clearCache,
    prefetch
  };
}